import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import i18n from './i18n/config';
import { initializeProfessionsCacheNew } from '@/services/professions/getProfessionsNew';

// Critical pages loaded eagerly (homepage and search - most common entry points)
import Index from '@/pages/Index';
import Search from '@/pages/Search';

// Lazy loaded pages for better code splitting
const Login = lazy(() => import('@/pages/Login'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const ProfessionalProfile = lazy(() => import('@/pages/ProfessionalProfile'));
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const Articles = lazy(() => import('@/pages/Articles'));
const ArticleDetail = lazy(() => import('@/pages/ArticleDetail'));
const FAQ = lazy(() => import('@/pages/FAQ'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const Accessibility = lazy(() => import('@/pages/Accessibility'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const AdminLogin = lazy(() => import('@/pages/AdminLogin'));
const Settings = lazy(() => import('@/pages/Settings'));
const MyRequests = lazy(() => import('@/pages/MyRequests'));
const AiRequest = lazy(() => import('@/pages/AiRequest'));
const WorkHistory = lazy(() => import('@/pages/WorkHistory'));
const WorkHistoryProfession = lazy(() => import('@/pages/WorkHistoryProfession'));
const HowItWorks = lazy(() => import('@/pages/HowItWorks'));
const BusinessCard = lazy(() => import('@/pages/BusinessCard'));
const QuickSearch = lazy(() => import('@/pages/QuickSearch'));

// Admin Pages (lazy loaded - rarely accessed)
const AdminDashboard = lazy(() => import('@/pages/Admin/AdminDashboard'));
const ProfessionalsManager = lazy(() => import('@/pages/Admin/ProfessionalsManager'));
const ArticlesManager = lazy(() => import('@/pages/Admin/ArticlesManager'));
const MessagesManager = lazy(() => import('@/pages/Admin/MessagesManager'));
const AdminSettings = lazy(() => import('@/pages/Admin/AdminSettings'));

// Context Providers
import { ThemeProvider } from '@/components/theme-provider';
import { AuthProvider } from '@/providers/AuthProvider';
import { DirectionProvider } from '@/providers/DirectionProvider';

// Components
import ScrollToTop from '@/components/ScrollToTop';
import { Toaster } from '@/components/ui/toaster';
import PhoneRequiredDialog from '@/components/auth/PhoneRequiredDialog';
import ErrorBoundary from '@/components/ui/error-boundary';
import BottomNavigation from '@/components/mobile/BottomNavigation';
import DiscountBanner from '@/components/discount/DiscountBanner';
import FloatingWhatsAppButton from '@/components/FloatingWhatsAppButton';
import { useDiscountEligibility } from '@/hooks/useDiscountEligibility';

// Loading fallback for lazy routes
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1, // Only retry failed queries once
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
    },
  },
});

// Separate component for discount banner (needs auth context)
const DiscountBannerWrapper: React.FC = () => {
  const { eligible, amount, message_he, loading } = useDiscountEligibility();

  if (loading || !eligible || !message_he) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[60]">
      <DiscountBanner amount={amount} message={message_he} />
    </div>
  );
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const isBusinessCard = location.pathname.startsWith('/card/');
  const isQuickSearch = location.pathname.startsWith('/quick-search');
  const isStandalone = isBusinessCard || isQuickSearch;
  const { t } = useTranslation();

  return (
    <>
      {/* Site-wide defaults. Helmet takes over the static tags in index.html, so a page without its own
          description still has one; a page's own Helmet renders later and wins. */}
      <Helmet>
        <title>{t('seo.homeTitle')}</title>
        <meta name="description" content={t('seo.homeDescription')} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="oFair - עופר" />
        <meta property="og:title" content={t('seo.homeTitle')} />
        <meta property="og:description" content={t('seo.homeDescription')} />
        <meta property="og:image" content="https://www.ofair.co.il/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('seo.homeTitle')} />
        <meta name="twitter:description" content={t('seo.homeDescription')} />
        <meta name="twitter:image" content="https://www.ofair.co.il/og-image.png" />
      </Helmet>
      <ScrollToTop />
      {!isStandalone && <DiscountBannerWrapper />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/chat" element={<AiRequest />} />
          <Route path="/search" element={<Search />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/professional/:id" element={<ProfessionalProfile />} />
          <Route path="/project/:id" element={<ProjectDetail />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/my-requests" element={<MyRequests />} />
          <Route path="/work-history" element={<WorkHistory />} />
          <Route path="/work-history/:profession" element={<WorkHistoryProfession />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/articles/:slug" element={<ArticleDetail />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/card/:slug" element={<BusinessCard />} />
          <Route path="/quick-search" element={<QuickSearch />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/professionals" element={<ProfessionalsManager />} />
          <Route path="/admin/articles" element={<ArticlesManager />} />
          <Route path="/admin/messages" element={<MessagesManager />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {!isStandalone && <PhoneRequiredDialog />}
      <Toaster />
      {!isStandalone && <FloatingWhatsAppButton />}
      {!isStandalone && <BottomNavigation />}
    </>
  );
};

function App() {
  // Pre-load professions cache in background for faster profession lookups
  useEffect(() => {
    initializeProfessionsCacheNew();
  }, []);

  return (
    <ErrorBoundary fallback={
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
        <div className="bg-white shadow-md rounded-lg p-6 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">{i18n.t('errors.criticalError')}</h1>
          <p className="mb-4">{i18n.t('errors.criticalErrorDescription')}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {i18n.t('errors.refreshPage')}
          </button>
        </div>
      </div>
    }>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <DirectionProvider>
            <ThemeProvider defaultTheme="light">
              <HelmetProvider>
                <BrowserRouter>
                    <AppContent />
                </BrowserRouter>
              </HelmetProvider>
            </ThemeProvider>
          </DirectionProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
